import React, { useEffect } from 'react'
import { AppProps } from '../../App'
import { Input } from '../../components/Input'
import { getRandomString, useEventListener } from '../../utils/functions'
import { useNavigate } from 'react-router-dom'
import Loading from '../../components/Loading'
import { useGoogleLogin } from '@react-oauth/google'
import { getItem, createItem, userSignIn } from '../../api/API'
import { storageKeys, tables } from '../../utils/constants'
import { UserModel } from '../../utils/interfaces'
import { useTranslation } from 'react-i18next'
import Navbar from '../../components/Navbar'
import Footer from '../../components/FooterLanding'

const SignIn = (props: AppProps) => {

  const { t } = useTranslation();
  const navigate = useNavigate();
  const submitRef = React.useRef<HTMLButtonElement>(null);
  const [username, setUsername] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [showPassword, setShowPassword] = React.useState(false);
  const [errorMessage, setErrorMessage] = React.useState('');
  const [isLoading, setIsLoading] = React.useState(false);

  useEffect(() => {
    if (props.user) {
      navigate('/');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.user]);

  useEventListener('keydown', (e) => {
    if (e.key === 'Enter') {
      submitRef.current?.click();
    }
  });

  // sign in
  const submit = async () => {
    setIsLoading(true);
    setErrorMessage('');
    const response = await userSignIn(username.replace('@', '-at-'), password);
    if (response.error) { // failed signing in
      setErrorMessage(t('auth.sign-in-error'));
    } else { // setup user when successfully signed in
      await setupUser(username);
    }
    setIsLoading(false);
  }

  // login with google
  const googleLogin = useGoogleLogin({
    onSuccess: async tokenResponse => {
      let [email, firstName] = ['', ''];
      // get user info from google
      try {
        const response = await (await fetch('https://www.googleapis.com/oauth2/v3/userinfo', {
          headers: {
            "Authorization": `Bearer ${tokenResponse.access_token}`
          }
        })).json();
        email = response.email;
        firstName = response.given_name || '';
      } catch (err) {
        console.error(err);
      }
      if (email) { // get user from database
        try {
          const data = await getItem(tables.users, email);
          if (data.item.id) { // user exists
            await setupUser(email, data);
          } else { // user doesn't exist -> create new user
            const newUser: UserModel = { id: email, id2: getRandomString(10), name: firstName, email, plan: { name: 'free' } };
            await createItem(tables.users, newUser);
            // await createItem(tables.metrics, { id: getRandomString(14), type: 'registration', date: new Date().toISOString() });
            // finished sign in
            props.setUser(newUser);
            localStorage.setItem(storageKeys.userId, email);
            localStorage.setItem(storageKeys.userId2, newUser.id2);
            props.setIsAIOpened(true);
          }
        } catch (error) { }
      }
    }
  })

  // setup user
  const setupUser = async (username: string, data?: any) => {
    if (!data) { // get user from database
      data = await getItem(tables.users, username);
    }
    props.setUser(data.item);
    localStorage.setItem(storageKeys.userId, username);
    localStorage.setItem(storageKeys.userId2, data.item?.id2 || '');
  }

  return (
    <>
      {props.user !== undefined && <Navbar {...props} />}
      <main className='my-20 flex items-center justify-center p-8'>
        <div className='form w-full max-w-xs'>
          <div className='text-2xl md:text-3xl'>{t('auth.sign-in')}</div>
          {errorMessage && <div className='text-xs md:text-sm text-red-400 mt-4'>{errorMessage}</div>}
          <div className='mt-4'>
            <label className='text-xs md:text-sm text-black/50 m-1'>Email</label>
            <Input autoFocus type='email' value={username} onChange={(e) => setUsername(e.target.value)} />
          </div>
          <div className='mt-2'>
            <label className='text-xs md:text-sm text-black/50 m-1'>{t('auth.password')}</label>
            <div className='relative w-full'>
              <Input id='password-input' type={showPassword ? 'text' : 'password'} value={password} onChange={(e) => setPassword(e.target.value)} />
              <div onClick={() => {
                setShowPassword(!showPassword);
                document.getElementById('password-input')?.focus();
              }} className='text-xs underline cursor-pointer absolute top-1/2 right-4 -translate-y-1/2'>{showPassword ? t('general.hide') : t('general.show')}</div>
            </div>
          </div>
          <button onClick={submit} ref={submitRef} disabled={isLoading || !username || !password} className='border flex items-center justify-center bg-primary border-primary disabled:border-gray-300 disabled:bg-gray-300 text-white text-sm md:text-base w-full py-1.5 mt-8 rounded-lg'>{isLoading ? <Loading size={6} /> : t('auth.sign-in')}</button>
          <button onClick={() => googleLogin()} className='flex items-center justify-center text-sm md:text-base w-full py-1.5 mt-6 rounded-lg border'><i className='fa-brands fa-google mr-2' />{t('auth.continue-with-google')}</button>
          <div onClick={() => navigate('/signup')} className='cursor-pointer text-center text-sm md:text-base hover:text-primary mt-8 underline'>{t('auth.create-account')}</div>
        </div>
      </main>
      <Footer />
    </>
  )
}

export default SignIn